import { type AnimationClip, type Object3D, type Scene, type SkinnedMesh } from 'three'
import { escape_html } from '../../html.ts'

export interface ExportValidationResult {
  valid: boolean
  errors: string[]
  warnings: string[]
}

export class ExportValidation {
  static validate_skinned_mesh_export (
    skinned_meshes: SkinnedMesh[],
    animation_clips: AnimationClip[]
  ): ExportValidationResult {
    const result = this.validate_animation_clips(animation_clips)

    if (skinned_meshes.length === 0) {
      result.errors.push('No skinned mesh is available to export.')
    }

    const meshes_without_bones = skinned_meshes.filter((mesh) => mesh.skeleton.bones.length === 0)
    if (meshes_without_bones.length > 0) {
      result.errors.push(`${meshes_without_bones.length} skinned mesh(es) have no skeleton bones.`)
    }

    return this.finalize(result)
  }

  static validate_scene_export (
    scene: Scene,
    animation_clips: AnimationClip[]
  ): ExportValidationResult {
    const result = this.validate_animation_clips(animation_clips)

    let skinned_mesh_count = 0
    let bone_count = 0
    scene.traverse((child: Object3D) => {
      if (child.type === 'SkinnedMesh') skinned_mesh_count++
      if (child.type === 'Bone') bone_count++
    })

    if (skinned_mesh_count === 0) {
      result.errors.push('No skinned mesh was found in the export scene.')
    }

    if (bone_count === 0) {
      result.errors.push('No skeleton bones were found in the export scene.')
    }

    return this.finalize(result)
  }

  static to_html (result: ExportValidationResult): string {
    const error_html = result.errors.length > 0
      ? `<h3>Cannot export yet</h3><ul>${result.errors.map(error => `<li>${escape_html(error)}</li>`).join('')}</ul>`
      : ''

    const warning_html = result.warnings.length > 0
      ? `<h3>Warnings</h3><ul>${result.warnings.map(warning => `<li>${escape_html(warning)}</li>`).join('')}</ul>`
      : ''

    return `
      <div class="export-validation-report">
        ${error_html}
        ${warning_html}
      </div>
    `
  }

  private static validate_animation_clips (animation_clips: AnimationClip[]): ExportValidationResult {
    const result: ExportValidationResult = {
      valid: true,
      errors: [],
      warnings: []
    }

    if (animation_clips.length === 0) {
      result.errors.push('No animation clips are selected for export.')
      return result
    }

    const clips_without_tracks = animation_clips.filter((clip) => clip.tracks.length === 0)
    if (clips_without_tracks.length > 0) {
      result.errors.push(`${clips_without_tracks.length} selected animation clip(s) contain no keyframe tracks.`)
    }

    const unnamed_clips = animation_clips.filter((clip) => clip.name.trim() === '')
    if (unnamed_clips.length > 0) {
      result.warnings.push(`${unnamed_clips.length} selected animation clip(s) have no name.`)
    }

    const zero_duration_clips = animation_clips.filter((clip) => clip.duration <= 0)
    if (zero_duration_clips.length > 0) {
      result.warnings.push(`${zero_duration_clips.length} selected animation clip(s) have zero duration.`)
    }

    return result
  }

  private static finalize (result: ExportValidationResult): ExportValidationResult {
    result.valid = result.errors.length === 0
    return result
  }
}

